import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';

import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import { trackedFunction } from 'reactiveweb/function';

import Component from 'umg-ember-backend/components/record-detail/for/default';
import A11yReport from 'umg-ember-backend/components/record-detail/forms/a11y-report';
import Toolbar from 'umg-ember-backend/components/record-detail/toolbar';
import Button from 'umg-ember-backend/components/ui/button';
import Loader from 'umg-ember-backend/components/ui/loader';
import Window from 'umg-ember-backend/components/ui/window';
import recordHeadlineOrNew from 'umg-ember-backend/helpers/record-headline-or-new';
import recordOverline from 'umg-ember-backend/helpers/record-overline';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

export default class RecordDetailForA11yReportComponent extends Component {
  // Services
  @service fetch;
  @service recordStatus;

  // Getter and setter
  get report() {
    return this.loadReportResults.value;
  }

  get isDisabled() {
    return this.recordStatus.isDisabled(this.args.model);
  }

  // Functions
  startScan = task(async (model) => {
    try {
      const response = await this.fetch.put(`/a11y/reports/${model.id}/scan`);

      await this.store.pushPayload(response);
      this.loadReportResults.retry();
      this.flashMessages.success('record_detail.for.a11y_report.scan_success');
    } catch (error) {
      this.flashMessages.serverError(error);
    }
  });

  // Functions
  loadReportResults = trackedFunction(this, async () => {
    if (!this.args.model.report) {
      return null;
    }

    return await this.fetch.request(this.args.model.report);
  });

  // Template
  <template>
    <Window
      @onBodyScroll={{fn (mut this.scrollPosition)}}
      @scrollPosition={{this.scrollPosition}}
      @isLoading={{@model.isLoading}}
      data-test-record-detail='a11y-report'
    >
      <:overline>
        {{recordOverline @model}}
      </:overline>

      <:title>
        {{recordHeadlineOrNew @model}}
      </:title>

      <:toolbar>
        <Toolbar
          @isCompact={{this.isScrolled}}
          @model={{@model}}
          @primaryActions={{hash onCancel=this.transitionToPrevBreadcrumb}}
        >
          <:additonalPrimaryActions>
            <Button
              @style='active'
              @label={{t 'record_detail.for.a11y_report.start_scan'}}
              @icon='share'
              @disabled={{or this.isDisabled this.startScan.isRunning}}
              {{on 'click' (perform this.startScan @model)}}
            />
          </:additonalPrimaryActions>
        </Toolbar>
      </:toolbar>

      <:body>
        {{#if (or this.startScan.isRunning this.loadReportResults.isPending)}}
          <Loader @isWaiting={{true}} />
        {{else if this.report}}
          <A11yReport
            @model={{this.report}}
            @recordType={{getRecordType @model}}
            @onSaveRecord={{fn this.saveRecord @model}}
            @showErrors={{this.history.showErrors}}
          />
        {{else}}
          {{or @model.error (t 'record_detail.for.a11y_report.unknown_error')}}
        {{/if}}
      </:body>
    </Window>
  </template>
}
